import React from "react";
import { DialogRoot } from "components/ui/dialog";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@chakra-ui/react";
import { localizeStatus, localizeSupplierType } from "utils/localization";
import DeleteSupplierQuotation from "views/admin/supplierQuotations/components/DeleteSupplierQuotation";

const BASE_URL = process.env.REACT_APP_ENDPOINT;

const fileLink = (path, label) => {
  if (!path) return "—";
  return (
    <a
      className="text-blue-500 hover:underline"
      href={`${BASE_URL}/${path}`}
      target="_blank"
      rel="noreferrer"
    >
      {label}
    </a>
  );
};

const DetailRow = ({ label, children }) => (
  <div className="rounded-md border p-3">
    <div className="mb-1 text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold">{children || "—"}</div>
  </div>
);

const formatLocation = (location) => {
  if (!location?.lat || !location?.lng) return "—";
  return `${Number(location.lat).toFixed(5)}, ${Number(location.lng).toFixed(5)}`;
};

const deliveryLocationText = (quotation) => {
  if (quotation?.address) return quotation.address;
  if (quotation?.location) return formatLocation(quotation.location);
  return "—";
};

const SupplierQuotationDetails = ({ quotation }) => (
  <DialogRoot dir="rtl">
    <DialogTrigger asChild>
      <button className="rounded-md bg-blueSecondary py-2 px-4 text-white transition-all hover:bg-brandLinear">
        تفاصيل
      </button>
    </DialogTrigger>
    <DialogContent className="fixed top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-gray-700 end-1/2 dark:bg-navy-800 dark:text-white">
      <DialogHeader>
        <DialogTitle className="text-center text-xl font-bold">
          تفاصيل طلب التوريد
        </DialogTitle>
      </DialogHeader>
      <DialogBody className="max-h-[70vh] overflow-y-auto py-4">
        <div className="grid grid-cols-2 gap-3">
          <DetailRow label="رقم الطلب">{quotation?.orderNumber}</DetailRow>
          <DetailRow label="العميل">{quotation?.user?.name}</DetailRow>
          <DetailRow label="رقم المستخدم">{quotation?.user?.phoneNumber}</DetailRow>
          <DetailRow label="نوع التوريد">
            {localizeSupplierType(quotation?.supplierType)}
          </DetailRow>
          <DetailRow label="اسم المشروع">{quotation?.projectName}</DetailRow>
          <DetailRow label="رقم أمر الشراء">
            {quotation?.purchaseOrderNumber}
          </DetailRow>
          <DetailRow label="موقع التوريد">
            {quotation?.supplierLocation
              ? `${quotation.supplierLocation.region || ""} - ${
                  quotation.supplierLocation.city || ""
                }`
              : "—"}
          </DetailRow>
          <DetailRow label="التوصيل">
            {quotation?.withDelivery ? "نعم" : "لا"}
          </DetailRow>
          <DetailRow label="عنوان التسليم">
            {deliveryLocationText(quotation)}
          </DetailRow>
          <DetailRow label="التركيب">
            {quotation?.withInstallation ? "نعم" : "لا"}
          </DetailRow>
          <DetailRow label="طلب الشراء">
            {fileLink(quotation?.purchaseRequestFile, "فتح الملف")}
          </DetailRow>
          <DetailRow label="المنتج إن وجد">
            {fileLink(quotation?.productFile, "فتح الملف")}
          </DetailRow>
          <DetailRow label="الشروط الأخرى">{quotation?.otherTerms}</DetailRow>
        </div>

        {quotation?.text && (
          <div className="mt-4 rounded-md border p-3">
            <div className="mb-1 text-xs text-gray-500">وصف الطلب</div>
            <p className="text-sm">{quotation.text}</p>
          </div>
        )}

        <div className="mt-4">
          <h3 className="mb-2 text-lg font-bold">عروض الموردين</h3>
          <div className="grid gap-3">
            {(quotation?.supplierOffers || []).length === 0 && (
              <div className="rounded-md border p-3 text-sm">لا توجد عروض</div>
            )}
            {(quotation?.supplierOffers || []).map((offer) => (
              <div key={offer.id || offer._id} className="rounded-md border p-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <DetailRow label="المورد">{offer?.user?.name}</DetailRow>
                  <DetailRow label="الحالة">
                    {localizeStatus(offer?.status)}
                  </DetailRow>
                  <DetailRow label="الإجمالي">
                    {offer?.totalPriceText || offer?.totalPriceNumeric}
                  </DetailRow>
                  <DetailRow label="مدة التسليم">
                    {offer?.deliveryDuration}
                  </DetailRow>
                  <DetailRow label="شروط الدفع">{offer?.paymentTerms}</DetailRow>
                  <DetailRow label="صلاحية العرض">{offer?.offerValidity}</DetailRow>
                  <DetailRow label="ملف العرض">
                    {fileLink(offer?.quotationFile, "فتح الملف")}
                  </DetailRow>
                  <DetailRow label="تفاصيل إضافية">
                    {offer?.otherDetails}
                  </DetailRow>
                </div>
              </div>
            ))}
          </div>
        </div>

        {quotation?.invoice && (
          <div className="mt-4">
            <h3 className="mb-2 text-lg font-bold">الفاتورة</h3>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="إيصال التحويل">
                {fileLink(quotation.invoice.transferReceipt, "فتح الملف")}
              </DetailRow>
              <DetailRow label="ملاحظة التحويل">
                {quotation.invoice.transferNote}
              </DetailRow>
              <DetailRow label="الفاتورة">
                {fileLink(quotation.invoice.invoiceFile, "فتح الملف")}
              </DetailRow>
              <DetailRow label="ملاحظة الفاتورة">
                {quotation.invoice.invoiceNote}
              </DetailRow>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogCloseTrigger />
    </DialogContent>
  </DialogRoot>
);

const SupplierQuotationsColumn = (refetch) => [
  {
    Header: "رقم الطلب",
    accessor: "orderNumber",
  },
  {
    Header: "العميل",
    accessor: "user.name",
    Cell: ({ row }) => row.original?.user?.name || "—",
  },
  {
    Header: "نوع التوريد",
    accessor: "supplierType",
    Cell: ({ row }) => localizeSupplierType(row.original?.supplierType),
  },
  {
    Header: "اسم المشروع",
    accessor: "projectName",
    Cell: ({ row }) => row.original?.projectName || "—",
  },
  {
    Header: "رقم أمر الشراء",
    accessor: "purchaseOrderNumber",
    Cell: ({ row }) => row.original?.purchaseOrderNumber || "—",
  },
  {
    Header: "موقع التسليم",
    accessor: "address",
    Cell: ({ row }) => {
      return deliveryLocationText(row.original);
    },
  },
  {
    Header: "توصيل",
    accessor: "withDelivery",
    Cell: ({ row }) => (row.original?.withDelivery ? "نعم" : "لا"),
  },
  {
    Header: "تركيب",
    accessor: "withInstallation",
    Cell: ({ row }) => (row.original?.withInstallation ? "نعم" : "لا"),
  },
  {
    Header: "عدد العروض",
    accessor: "supplierOffers",
    Cell: ({ row }) => (row.original?.supplierOffers || []).length,
  },
  {
    Header: "المورد المختار",
    accessor: "chosenSupplier",
    Cell: ({ row }) => {
      const c = row.original?.chosenSupplier;
      return c ? c.name : "—";
    },
  },
  {
    Header: "الفاتورة",
    accessor: "invoice",
    Cell: ({ row }) => {
      const i = row.original?.invoice;
      if (!i) return "—";
      return (
        <div className="flex flex-col gap-1 text-xs">
          {i.transferReceipt && fileLink(i.transferReceipt, "إيصال التحويل")}
          {i.invoiceFile && fileLink(i.invoiceFile, "الفاتورة")}
        </div>
      );
    },
  },
  {
    Header: "التاريخ",
    accessor: "createdAt",
    Cell: ({ row }) =>
      row.original?.createdAt
        ? new Date(row.original.createdAt).toLocaleDateString("ar-EG")
        : "—",
  },
  {
    Header: "التفاصيل",
    Cell: ({ row }) => <SupplierQuotationDetails quotation={row.original} />,
  },
  {
    Header: "إجراء",
    Cell: ({ row }) => (
      <DeleteSupplierQuotation id={row.original.id} refetch={refetch} />
    ),
  },
];

export default SupplierQuotationsColumn;
